import {
  CohortBaseDetail,
  MyCohortPreviewResponseProjectHubUrl,
  MyCohortProgressEnum,
} from '@platform/client/schemas';
import { navigate } from 'wouter/use-browser-location';
import { CohortTimeline, TimelineItem } from './CohortTimeline';
import { getPanelContent, STEP_LABELS } from './panelContent';
import { getProgressItemStage } from './progressStage';
import { cohortDatesToStrings } from '../../util/ProgramHelpers';

type CohortDateStrings = ReturnType<typeof cohortDatesToStrings>;

/** The timeline steps in display order, and how each one is labelled and linked. */
const STEPS: {
  step: MyCohortProgressEnum;
  dateKey: keyof CohortDateStrings;
  isCalendarEvent?: boolean;
  /** Whether the step links out to the project hub while it is in progress. */
  hasProjectHubCta?: boolean;
}[] = [
  { step: 'kickoff', dateKey: 'kickoff', isCalendarEvent: true },
  { step: 'matching', dateKey: 'matchingRange' },
  { step: 'planning', dateKey: 'planningRange', hasProjectHubCta: true },
  { step: 'action', dateKey: 'actionRange', hasProjectHubCta: true },
  { step: 'reflect', dateKey: 'reflect', hasProjectHubCta: true },
  { step: 'wrap_up', dateKey: 'wrapUp', isCalendarEvent: true },
];

export const TimelineSection = ({
  cohortData,
  userProgress,
  projectHubUrl,
}: {
  cohortData: CohortBaseDetail;
  userProgress: MyCohortProgressEnum;
  projectHubUrl: MyCohortPreviewResponseProjectHubUrl;
}) => {
  const dateStrings = cohortDatesToStrings(cohortData);

  // Teams and match-assigned cohorts have their project chosen for them, so they never see Matching
  const choosesOwnProject = ['Individual', 'Self-Paced - Individual'].includes(
    cohortData.cohortType,
  );

  // Only provided by API once an assignment/team_assignment relevant to this cohort exists.
  // Currently informed by API as these pages key off of different ids (Individual: discovery_id, Teams: project_id). If we ever unify the ids, this will be a non-issue. One can dream.
  const projectHubCta =
    projectHubUrl ?
      { label: 'View in Project Hub', onClick: () => navigate(projectHubUrl) }
    : undefined;

  const items: TimelineItem[] = STEPS.filter(
    ({ step }) => step !== 'matching' || choosesOwnProject,
  ).map(({ step, dateKey, isCalendarEvent, hasProjectHubCta }) => {
    const stage = getProgressItemStage(userProgress, step);
    return {
      id: step,
      title: STEP_LABELS[step],
      subtitle: dateStrings[dateKey],
      stage,
      isCalendarEvent,
      panel: {
        ...getPanelContent(cohortData.cohortType, step),
        cta: hasProjectHubCta && stage === 'in-progress' ? projectHubCta : undefined,
      },
    };
  });

  return <CohortTimeline items={items} />;
};
